import React, { useState, useEffect } from 'react';
import { FaLock, FaPlus, FaTrash, FaCheckCircle, FaSpinner, FaFilter, FaList, FaSave, FaUsers, FaBuilding, FaEdit, FaTimes, FaCheck } from 'react-icons/fa';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import { getApiUrl } from '../utils/config';
import { getActionErrorMessage } from '../utils/errorHandler';
import '../styles/SearchableDropdown.css';
import { MATCH_BY_INSTITUTION, getCurriculumMatchBy } from '../utils/rankUtils';

const ConstraintsFormModern = () => {
  const [curricula, setCurricula] = useState([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState('');
  // Internally still split into two buckets (pharmd-style = experience-matched,
  // spep-style = institution-matched) since only one applies to any curriculum —
  // see curriculumMatchBy, which drives which bucket is shown/edited/submitted.
  const [pharmdConstraints, setPharmdConstraints] = useState([]);
  const [spepConstraints, setSpepConstraints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pharmd');
  const [ranks, setRanks] = useState([]);
  // curriculum_id -> rank object, so we know each curriculum's matchBy
  const [curriculumRankById, setCurriculumRankById] = useState(new Map());
  const [availableData, setAvailableData] = useState({ specialities: [], institutions: [] });
  const [availableGroups, setAvailableGroups] = useState({ specialityGroups: [], institutionGroups: [] });
  const [newConstraint, setNewConstraint] = useState({
    type: 'pharmd',
    minimumNumber: 1,
    entities: [],
    selectedGroups: [],
    fromRotation: 1,
    toRotation: 8
  });
  const [editingConstraint, setEditingConstraint] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownType, setDropdownType] = useState(null);

  useEffect(() => {
    fetchConstraints();
    fetchSpecialitiesAndInstitutions();
  }, []);

  // The constraints API returns a raw array of constraint documents (one per curriculum,
  // some legacy docs keyed by `curriculumId` instead of `curriculum_id`). Curriculum display
  // names come from a separate endpoint since constraint docs don't carry a name.
  const transformConstraintsData = (data, curriculumNameById) => {
    if (!Array.isArray(data)) return [];
    return data
      .map((doc) => {
        const curriculumId = doc.curriculum_id || doc.curriculumId || (typeof doc._id === 'string' ? doc._id.replace(/_constraints$/, '') : null);
        if (!curriculumId) return null;
        return {
          curriculum_id: curriculumId,
          curriculum_name: curriculumNameById.get(curriculumId) || curriculumId,
          pharmdConstraints: doc.pharmdConstraints || [],
          spepConstraints: doc.spepConstraints || []
        };
      })
      .filter(Boolean);
  };

  const fetchCurriculumRankData = async () => {
    try {
      const [curriculumsRes, ranksRes] = await Promise.all([
        fetch(getApiUrl('curriculum/curriculums'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        }),
        fetch(getApiUrl('ranks/ranks'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        })
      ]);

      const curriculumsList = curriculumsRes.ok ? await curriculumsRes.json() : [];
      const ranksList = ranksRes.ok ? await ranksRes.json() : [];
      setRanks(Array.isArray(ranksList) ? ranksList : []);

      const nameById = new Map();
      const rankById = new Map();
      (Array.isArray(curriculumsList) ? curriculumsList : []).forEach((c) => {
        nameById.set(c._id, c.name || c._id);
        rankById.set(c._id, { rankId: c.rankId, rank: c.rank });
      });
      setCurriculumRankById(rankById);
      return nameById;
    } catch (_err) {
      return new Map();
    }
  };

  const fetchConstraints = async () => {
    try {
      setIsLoading(true);
      const [response, curriculumNameById] = await Promise.all([
        fetch(getApiUrl('constraints/get_constraints/')),
        fetchCurriculumRankData()
      ]);

      if (!response.ok) {
        throw new Error('Failed to fetch constraints');
      }

      const data = await response.json();
      if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Raw constraints data:', data);

      const transformedData = transformConstraintsData(data, curriculumNameById);

      if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Transformed constraints data:', transformedData);

      // Keep the full curriculum objects for the dropdown
      setCurricula(transformedData);

      if (transformedData.length > 0) {
        const defaultCurriculum = transformedData[0].curriculum_id;
        setSelectedCurriculum(defaultCurriculum);

        const curriculumData = transformedData.find(item => item.curriculum_id === defaultCurriculum);
        if (curriculumData) {
          setPharmdConstraints(curriculumData.pharmdConstraints || []);
          setSpepConstraints(curriculumData.spepConstraints || []);
        }
      }

      setIsLoading(false);
    } catch (err) {
      setError(getActionErrorMessage(err, 'load constraints'));
      setIsLoading(false);
    }
  };

  // matchBy for the currently selected curriculum ('experience' or 'institution'),
  // and the internal tab name it maps to.
  const selectedCurriculumMatchBy = getCurriculumMatchBy(
    ranks,
    (() => {
      const info = curriculumRankById.get(selectedCurriculum);
      return info ? { rankId: info.rankId, rank: info.rank } : null;
    })()
  );
  const requiredTab = selectedCurriculumMatchBy === MATCH_BY_INSTITUTION ? 'spep' : 'pharmd';
  const selectedCurriculumRankName = curriculumRankById.get(selectedCurriculum)?.rank
    || (selectedCurriculumMatchBy === MATCH_BY_INSTITUTION ? 'Institution' : 'Experience');

  // A curriculum has exactly one rank, so its constraints always target that
  // rank's entity type — keep activeTab in step rather than letting it drift.
  useEffect(() => {
    if (selectedCurriculum && activeTab !== requiredTab) {
      setActiveTab(requiredTab);
    }
  }, [selectedCurriculum, requiredTab]);

  const fetchSpecialitiesAndInstitutions = async () => {
    try {
      // Fetch specialities for PharmD constraints
      const specialitiesResponse = await fetch(getApiUrl('crud/get_all_specialities'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      // Fetch institutions for SPEP constraints  
      const institutionsResponse = await fetch(getApiUrl('crud/get_all_institutions'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (specialitiesResponse.ok && institutionsResponse.ok) {
        const specialitiesData = await specialitiesResponse.json();
        const institutionsData = await institutionsResponse.json();
        
        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.log('Specialities data:', specialitiesData);
            console.log('Institutions data:', institutionsData);
        }
        
        setAvailableData({
          specialities: specialitiesData.map(spec => ({
            id: spec._id,
            name: spec.speciality,
            groups: spec.groups || []
          })),
          institutions: institutionsData.map(inst => ({
            id: inst._id,
            name: inst.name,
            groups: inst.groups || []
          }))
        });

        // Extract unique groups - handle both array and string formats
        const specialityGroups = [...new Set(
          specialitiesData
            .filter(spec => spec.groups && (Array.isArray(spec.groups) ? spec.groups.length > 0 : spec.groups))
            .flatMap(spec => {
              if (Array.isArray(spec.groups)) {
                return spec.groups;
              } else if (typeof spec.groups === 'string') {
                return [spec.groups];
              }
              return [];
            })
        )].sort();
        
        const institutionGroups = [...new Set(
          institutionsData
            .filter(inst => inst.groups && (Array.isArray(inst.groups) ? inst.groups.length > 0 : inst.groups))
            .flatMap(inst => {
              if (Array.isArray(inst.groups)) {
                return inst.groups;
              } else if (typeof inst.groups === 'string') {
                return [inst.groups];
              }
              return [];
            })
        )].sort();
        
        if (import.meta.env.VITE_DEBUG_MODE === 'true') {
            console.log('Extracted speciality groups:', specialityGroups);
            console.log('Extracted institution groups:', institutionGroups);
        }
        
        setAvailableGroups({ specialityGroups, institutionGroups });
      }
    } catch (err) {
      setError('Failed to fetch specialities and institutions: ' + err.message);
    }
  };

  const handleCurriculumChange = async (curriculumId) => {
    setSelectedCurriculum(curriculumId);

    // Constraints for every curriculum are already loaded in `curricula` (from fetchConstraints);
    // just look up the one the user picked instead of re-fetching and re-parsing everything.
    const curriculumData = curricula.find(item => item.curriculum_id === curriculumId);

    if (curriculumData) {
      setPharmdConstraints(curriculumData.pharmdConstraints || []);
      setSpepConstraints(curriculumData.spepConstraints || []);
    } else {
      setPharmdConstraints([]);
      setSpepConstraints([]);
    }
  };

  const addConstraint = () => {
    const constraint = {
      id: Date.now(),
      minimumNumber: newConstraint.minimumNumber,
      entities: [...newConstraint.entities],
      selectedGroups: [...newConstraint.selectedGroups],
      fromRotation: newConstraint.fromRotation,
      toRotation: newConstraint.toRotation
    };

    if (activeTab === 'pharmd') {
      setPharmdConstraints([...pharmdConstraints, constraint]);
    } else {
      setSpepConstraints([...spepConstraints, constraint]);
    }

    resetNewConstraint();
  };

  const editConstraint = (constraint) => {
    setEditingConstraint({
      ...constraint,
      originalId: constraint.id,
      // Ensure selectedGroups exists as an array
      selectedGroups: constraint.selectedGroups || [],
      // Ensure entities exists as an array
      entities: constraint.entities || []
    });
  };

  const saveEditConstraint = () => {
    if (activeTab === 'pharmd') {
      setPharmdConstraints(pharmdConstraints.map(c => 
        c.id === editingConstraint.originalId ? { ...editingConstraint, id: editingConstraint.originalId } : c
      ));
    } else {
      setSpepConstraints(spepConstraints.map(c => 
        c.id === editingConstraint.originalId ? { ...editingConstraint, id: editingConstraint.originalId } : c
      ));
    }
    setEditingConstraint(null);
  };

  const cancelEditConstraint = () => {
    setEditingConstraint(null);
  };

  const removeConstraint = (id) => {
    if (activeTab === 'pharmd') {
      setPharmdConstraints(pharmdConstraints.filter(c => c.id !== id));
    } else {
      setSpepConstraints(spepConstraints.filter(c => c.id !== id));
    }
  };

  const resetNewConstraint = () => {
    setNewConstraint({
      type: activeTab,
      minimumNumber: 1,
      entities: [],
      selectedGroups: [],
      fromRotation: 1,
      toRotation: 8
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      
              const response = await fetch(getApiUrl(`constraints/set_constraints_by_curriculum/${selectedCurriculum}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          // Only the bucket matching this curriculum's rank has entries; the
          // backend derives the entity type (speciality/institution) from the
          // curriculum's rank rather than trusting a client-supplied bucket name.
          constraints: currentConstraints
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update constraints');
      }
      
      setSuccessMessage('Constraints updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsLoading(false);
    } catch (err) {
      setError(getActionErrorMessage(err, 'update constraints'));
      setIsLoading(false);
    }
  };

  const toggleEntitySelection = (entity, isEditing = false) => {
    const targetConstraint = isEditing ? editingConstraint : newConstraint;
    const setTargetConstraint = isEditing ? setEditingConstraint : setNewConstraint;
    
    const entities = targetConstraint.entities || [];
    const isSelected = entities.some(e => e.id === entity.id);
    if (isSelected) {
      setTargetConstraint({
        ...targetConstraint,
        entities: entities.filter(e => e.id !== entity.id)
      });
    } else {
      setTargetConstraint({
        ...targetConstraint,
        entities: [...entities, entity]
      });
    }
  };

  const toggleGroupSelection = (group, isEditing = false) => {
    const targetConstraint = isEditing ? editingConstraint : newConstraint;
    const setTargetConstraint = isEditing ? setEditingConstraint : setNewConstraint;
    
    const selectedGroups = targetConstraint.selectedGroups || [];
    const isSelected = selectedGroups.includes(group);
    if (isSelected) {
      setTargetConstraint({
        ...targetConstraint,
        selectedGroups: selectedGroups.filter(g => g !== group)
      });
    } else {
      setTargetConstraint({
        ...targetConstraint,
        selectedGroups: [...selectedGroups, group]
      });
    }
  };

  // Helper functions to get names
  const getSpecialityName = (specialityId) => {
    const speciality = availableData.specialities.find(s => s.id === specialityId);
    return speciality ? speciality.name : `Unknown Speciality (${specialityId})`;
  };

  const getInstitutionName = (institutionId) => {
    const institution = availableData.institutions.find(i => i.id === institutionId);
    return institution ? institution.name : `Unknown Institution (${institutionId})`;
  };

  const getCurrentEntities = () => {
    return activeTab === 'pharmd' ? availableData.specialities : availableData.institutions;
  };

  const getCurrentGroups = () => {
    const groups = activeTab === 'pharmd' ? availableGroups.specialityGroups : availableGroups.institutionGroups;
    if (import.meta.env.VITE_DEBUG_MODE === 'true') {
        console.log(`Getting ${activeTab} groups:`, groups);
        console.log('Available groups state:', availableGroups);
    }
    return groups;
  };

  const getEntityDisplayName = (entity) => {
    return activeTab === 'pharmd' ? getSpecialityName(entity.id) : getInstitutionName(entity.id);
  };

  const currentConstraints = activeTab === 'pharmd' ? pharmdConstraints : spepConstraints;

  if (isLoading && curricula.length === 0) {
    return (
      <div style={{
        background: '#f9fafb',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <LoadingSpinner text="Loading constraints..." variant="card" />
      </div>
    );
  }

  return (
    <div style={{
      background: '#f9fafb',
      minHeight: '100vh',
      padding: '2rem',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header Section */}
      <div style={{
        textAlign: 'center',
        marginBottom: '2rem',
        padding: '2rem',
        background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
        borderRadius: '18px',
        color: 'white',
        boxShadow: '0 12px 35px rgba(43, 70, 99, 0.25)',
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '1400px',
        margin: '0 auto 2rem'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '0.5rem'
          }}>
            <FaLock style={{ fontSize: '2.5rem', color: 'white' }} />
            <h1 style={{ 
              fontSize: '2.2rem',
              fontWeight: 700,
              margin: 0,
              color: 'white',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              letterSpacing: '-0.5px'
            }}>
              Constraints Management
            </h1>
          </div>
          <p style={{ 
            margin: 0, 
            fontSize: '1.1rem', 
            opacity: 0.9,
            lineHeight: 1.4
          }}>
            Define and manage constraints for PharmD and SPEP programs with advanced filtering and validation
          </p>
        </div>
      </div>
      
      {/* Alert Messages */}
      {error && (
        <div style={{
          background: 'rgba(248, 113, 113, 0.1)',
          border: '2px solid #f87171',
          borderRadius: '16px',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          backdropFilter: 'blur(10px)',
          maxWidth: '1400px',
          margin: '0 auto 2rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>⚠️</span>
          <span style={{ color: '#dc2626', fontWeight: 600 }}>{error}</span>
        </div>
      )}

      {successMessage && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '2px solid #22c55e',
          borderRadius: '16px',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          backdropFilter: 'blur(10px)',
          maxWidth: '1400px',
          margin: '0 auto 2rem'
        }}>
          <span style={{ fontSize: '1.5rem' }}>✅</span>
          <span style={{ color: '#059669', fontWeight: 600 }}>{successMessage}</span>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'grid',
        gap: '2rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Curriculum Selection */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '2.5rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '16px',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FaFilter style={{ fontSize: '1.5rem', color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#2d3748' }}>
                📚 Curricula Selection
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', color: '#718096', fontSize: '1rem' }}>
                Choose a curriculum to manage its constraints
              </p>
            </div>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.75rem', 
              fontWeight: 600, 
              color: '#374151',
              fontSize: '1rem'
            }}>
              Select Curriculum
            </label>
            <SearchableDropdown
              options={curricula}
              value={selectedCurriculum}
              onChange={handleCurriculumChange}
              placeholder="Select a curriculum..."
              disabled={isLoading}
              size="large"
              optionLabel="curriculum_name"
              optionValue="curriculum_id"
            />
          </div>
        </div>

        {selectedCurriculum && (
          <>
            {/* Match-type banner — a curriculum has exactly one rank, so its
                constraints always target that rank's entity type. There is no
                tab to switch: this just states which type applies. */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
              borderRadius: '12px',
              padding: '16px 24px',
              marginBottom: '30px',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
              color: 'white',
              fontWeight: 600,
              fontSize: '1rem'
            }}>
              <span style={{ fontSize: '1.2rem' }}>{activeTab === 'spep' ? '🏢' : '🏥'}</span>
              {selectedCurriculumRankName} Constraints ({currentConstraints.length})
            </div>

            {/* Add New Constraint Form */}
            <div style={{
              background: 'rgba(16, 185, 129, 0.05)',
              borderRadius: '16px',
              padding: '2rem',
              border: '2px solid rgba(16, 185, 129, 0.2)'
            }}>
              <h4 style={{ 
                margin: '0 0 1.5rem 0', 
                fontSize: '1.25rem', 
                fontWeight: 700, 
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <FaPlus />
                Add New {selectedCurriculumRankName} Constraint
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontWeight: 600, 
                    color: '#059669',
                    fontSize: '0.9rem'
                  }}>
                    Minimum Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newConstraint.minimumNumber}
                    onChange={(e) => setNewConstraint({...newConstraint, minimumNumber: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #10b981',
                      borderRadius: '10px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      background: 'white',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontWeight: 600, 
                    color: '#059669',
                    fontSize: '0.9rem'
                  }}>
                    From Rotation
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={newConstraint.fromRotation}
                    onChange={(e) => setNewConstraint({...newConstraint, fromRotation: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #10b981',
                      borderRadius: '10px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      background: 'white',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontWeight: 600, 
                    color: '#059669',
                    fontSize: '0.9rem'
                  }}>
                    To Rotation
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={newConstraint.toRotation}
                    onChange={(e) => setNewConstraint({...newConstraint, toRotation: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #10b981',
                      borderRadius: '10px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      background: 'white',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Speciality/Institution Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#059669' }}>
                  Select {activeTab === 'pharmd' ? 'Specialities' : 'Institutions'}:
                </h5>
                <SearchableDropdown
                  options={getCurrentEntities()}
                  selectedValues={newConstraint.entities.map(e => e.id)}
                  onSelectionChange={(selectedIds) => {
                    const selectedEntities = getCurrentEntities().filter(entity => selectedIds.includes(entity.id));
                    setNewConstraint({...newConstraint, entities: selectedEntities});
                  }}
                  placeholder={`Select ${activeTab === 'pharmd' ? 'specialities' : 'institutions'}...`}
                  multiple={true}
                  optionLabel="name"
                  optionValue="id"
                  size="large"
                />
                {newConstraint.entities.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#059669' }}>
                      Selected {activeTab === 'pharmd' ? 'Specialities' : 'Institutions'}:
                    </h6>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {newConstraint.entities.map(entity => (
                        <span key={entity.id} style={{
                          background: '#10b981',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }} onClick={() => toggleEntitySelection(entity)}>
                          {entity.name} ✕
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Group Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#059669' }}>
                  Select Groups (Optional):
                </h5>
                {/* Debug info */}
                <div style={{ 
                  background: 'rgba(156, 163, 175, 0.1)', 
                  padding: '0.5rem', 
                  borderRadius: '8px', 
                  marginBottom: '0.5rem',
                  fontSize: '0.8rem',
                  color: '#6b7280'
                }}>
                  Available groups: {getCurrentGroups().length} groups found
                  {getCurrentGroups().length > 0 && (
                    <div style={{ marginTop: '0.25rem' }}>
                      {getCurrentGroups().slice(0, 5).join(', ')}
                      {getCurrentGroups().length > 5 && ` and ${getCurrentGroups().length - 5} more...`}
                    </div>
                  )}
                </div>
                <SearchableDropdown
                  options={getCurrentGroups().map(group => ({ id: group, name: group }))}
                  selectedValues={newConstraint.selectedGroups}
                  onSelectionChange={(selectedGroups) => {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Groups selected:', selectedGroups);
                    
                    // Get the previous groups to see what was removed
                    const previousGroups = newConstraint.selectedGroups || [];
                    const addedGroups = selectedGroups.filter(group => !previousGroups.includes(group));
                    const removedGroups = previousGroups.filter(group => !selectedGroups.includes(group));
                    
                    // Add entities for newly selected groups
                    let updatedEntities = [...newConstraint.entities];
                    
                    // Add entities for new groups
                    addedGroups.forEach(group => {
                      const entitiesWithGroup = getCurrentEntities().filter(entity => {
                        if (Array.isArray(entity.groups)) {
                          return entity.groups.includes(group);
                        } else if (typeof entity.groups === 'string') {
                          return entity.groups === group;
                        }
                        return false;
                      });
                      
                      // Add these entities if they're not already selected
                      entitiesWithGroup.forEach(entity => {
                        if (!updatedEntities.some(e => e.id === entity.id)) {
                          updatedEntities.push(entity);
                        }
                      });
                    });
                    
                    // Remove entities for removed groups (only if they're not in other selected groups)
                    removedGroups.forEach(group => {
                      const remainingGroups = selectedGroups.filter(g => g !== group);
                      updatedEntities = updatedEntities.filter(entity => {
                        // Keep entity if it belongs to any remaining selected group
                        if (remainingGroups.length === 0) return true;
                        
                        if (Array.isArray(entity.groups)) {
                          return remainingGroups.some(g => entity.groups.includes(g));
                        } else if (typeof entity.groups === 'string') {
                          return remainingGroups.includes(entity.groups);
                        }
                        return true;
                      });
                    });
                    
                    setNewConstraint({
                      ...newConstraint, 
                      selectedGroups: selectedGroups,
                      entities: updatedEntities
                    });
                  }}
                  placeholder="Select groups..."
                  multiple={true}
                  optionLabel="name"
                  optionValue="id"
                  size="large"
                />
                {newConstraint.selectedGroups.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#059669' }}>
                      Selected Groups:
                    </h6>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {newConstraint.selectedGroups.map(group => (
                        <span key={group} style={{
                          background: '#059669',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }} onClick={() => toggleGroupSelection(group)}>
                          {group} ✕
                        </span>
                      ))}
                    </div>
                    
                    {/* Preview of entities that will be added by groups */}
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                      <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#059669' }}>
                        Entities from selected groups:
                      </h6>
                      <div style={{ fontSize: '0.8rem', color: '#059669' }}>
                        {(() => {
                          const groupEntities = newConstraint.selectedGroups.flatMap(group => {
                            return getCurrentEntities().filter(entity => {
                              if (Array.isArray(entity.groups)) {
                                return entity.groups.includes(group);
                              } else if (typeof entity.groups === 'string') {
                                return entity.groups === group;
                              }
                              return false;
                            });
                          });
                          
                          const uniqueEntities = [...new Set(groupEntities.map(e => e.id))].map(id => 
                            groupEntities.find(e => e.id === id)
                          );
                          
                          if (uniqueEntities.length === 0) {
                            return 'No entities found for selected groups';
                          }
                          
                          return uniqueEntities.map(entity => entity.name).join(', ');
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={addConstraint}
                disabled={newConstraint.entities.length === 0 && newConstraint.selectedGroups.length === 0}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.75rem 1.5rem',
                  color: 'white',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s ease',
                  opacity: newConstraint.entities.length === 0 && newConstraint.selectedGroups.length === 0 ? 0.5 : 1
                }}
              >
                <FaPlus />
                Add Constraint
              </button>
            </div>

            {/* Current Constraints */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '16px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FaList style={{ fontSize: '1.5rem', color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#2d3748' }}>
                    📋 Current {selectedCurriculumRankName} Constraints
                  </h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#718096', fontSize: '1rem' }}>
                    {currentConstraints.length} constraints configured
                  </p>
                </div>
              </div>

              {currentConstraints.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: 'rgba(156, 163, 175, 0.1)',
                  borderRadius: '16px',
                  border: '2px dashed #9ca3af'
                }}>
                  <FaList style={{ fontSize: '3rem', color: '#9ca3af', marginBottom: '1rem' }} />
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '1.25rem' }}>
                    No constraints configured
                  </h4>
                  <p style={{ margin: 0, color: '#9ca3af' }}>
                    Add your first {selectedCurriculumRankName} constraint above
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {currentConstraints.map((constraint, index) => (
                    <div key={constraint.id || index} style={{
                      background: 'rgba(102, 126, 234, 0.05)',
                      borderRadius: '16px',
                      padding: '2rem',
                      border: '2px solid rgba(102, 126, 234, 0.2)',
                    }}>
                      {editingConstraint && editingConstraint.originalId === constraint.id ? (
                        // Edit Mode
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '1.5rem' 
                          }}>
                            <h4 style={{ 
                              margin: 0, 
                              fontSize: '1.1rem', 
                              fontWeight: 600, 
                              color: '#667eea' 
                            }}>
                              ✏️ Editing Constraint #{index + 1}
                            </h4>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={saveEditConstraint}
                                style={{
                                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.5rem',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaCheck />
                              </button>
                              <button
                                onClick={cancelEditConstraint}
                                style={{
                                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.5rem',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                          
                          {/* Edit Form */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                                Minimum Number
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={editingConstraint.minimumNumber || 1}
                                onChange={(e) => setEditingConstraint({...editingConstraint, minimumNumber: parseInt(e.target.value) || 1})}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #667eea',
                                  borderRadius: '8px',
                                  fontSize: '0.9rem',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                                From Rotation
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="8"
                                value={editingConstraint.fromRotation || 1}
                                onChange={(e) => setEditingConstraint({...editingConstraint, fromRotation: parseInt(e.target.value) || 1})}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #667eea',
                                  borderRadius: '8px',
                                  fontSize: '0.9rem',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                                To Rotation
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="8"
                                value={editingConstraint.toRotation || 8}
                                onChange={(e) => setEditingConstraint({...editingConstraint, toRotation: parseInt(e.target.value) || 8})}
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: '2px solid #667eea',
                                  borderRadius: '8px',
                                  fontSize: '0.9rem',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </div>
                          </div>

                          {/* Edit specialities/institutions selection */}
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>
                              Select {activeTab === 'pharmd' ? 'Specialities' : 'Institutions'}:
                            </h5>
                            <SearchableDropdown
                              options={getCurrentEntities()}
                              selectedValues={(editingConstraint.entities || []).map(e => e.id)}
                              onSelectionChange={(selectedIds) => {
                                const selectedEntities = getCurrentEntities().filter(entity => selectedIds.includes(entity.id));
                                setEditingConstraint({...editingConstraint, entities: selectedEntities});
                              }}
                              placeholder={`Select ${activeTab === 'pharmd' ? 'specialities' : 'institutions'}...`}
                              multiple={true}
                              optionLabel="name"
                              optionValue="id"
                              size="large"
                            />
                            {(editingConstraint.entities || []).length > 0 && (
                              <div style={{ marginTop: '1rem' }}>
                                <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                  Selected {activeTab === 'pharmd' ? 'Specialities' : 'Institutions'}:
                                </h6>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                  {(editingConstraint.entities || []).map(entity => (
                                    <span key={entity.id} style={{
                                      background: '#667eea',
                                      color: 'white',
                                      padding: '0.5rem 1rem',
                                      borderRadius: '20px',
                                      fontSize: '0.85rem',
                                      fontWeight: 500,
                                      cursor: 'pointer'
                                    }} onClick={() => toggleEntitySelection(entity, true)}>
                                      {getEntityDisplayName(entity)} ✕
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Edit groups selection */}
                          <div>
                            <h5 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>
                              Select Groups:
                            </h5>
                            {/* Debug info for edit mode */}
                            <div style={{ 
                              background: 'rgba(156, 163, 175, 0.1)', 
                              padding: '0.5rem', 
                              borderRadius: '8px', 
                              marginBottom: '0.5rem',
                              fontSize: '0.8rem',
                              color: '#6b7280'
                            }}>
                              Available groups: {getCurrentGroups().length} groups found
                              {getCurrentGroups().length > 0 && (
                                <div style={{ marginTop: '0.25rem' }}>
                                  {getCurrentGroups().slice(0, 5).join(', ')}
                                  {getCurrentGroups().length > 5 && ` and ${getCurrentGroups().length - 5} more...`}
                                </div>
                              )}
                            </div>
                            <SearchableDropdown
                              options={getCurrentGroups().map(group => ({ id: group, name: group }))}
                              selectedValues={editingConstraint?.selectedGroups || []}
                              onSelectionChange={(selectedGroups) => {
                                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Edit mode - Groups selected:', selectedGroups);
                                
                                // Get the previous groups to see what was removed
                                const previousGroups = editingConstraint?.selectedGroups || [];
                                const addedGroups = selectedGroups.filter(group => !previousGroups.includes(group));
                                const removedGroups = previousGroups.filter(group => !selectedGroups.includes(group));
                                
                                // Add entities for newly selected groups
                                let updatedEntities = [...(editingConstraint?.entities || [])];
                                
                                // Add entities for new groups
                                addedGroups.forEach(group => {
                                  const entitiesWithGroup = getCurrentEntities().filter(entity => {
                                    if (Array.isArray(entity.groups)) {
                                      return entity.groups.includes(group);
                                    } else if (typeof entity.groups === 'string') {
                                      return entity.groups === group;
                                    }
                                    return false;
                                  });
                                  
                                  // Add these entities if they're not already selected
                                  entitiesWithGroup.forEach(entity => {
                                    if (!updatedEntities.some(e => e.id === entity.id)) {
                                      updatedEntities.push(entity);
                                    }
                                  });
                                });
                                
                                // Remove entities for removed groups (only if they're not in other selected groups)
                                removedGroups.forEach(group => {
                                  const remainingGroups = selectedGroups.filter(g => g !== group);
                                  updatedEntities = updatedEntities.filter(entity => {
                                    // Keep entity if it belongs to any remaining selected group
                                    if (remainingGroups.length === 0) return true;
                                    
                                    if (Array.isArray(entity.groups)) {
                                      return remainingGroups.some(g => entity.groups.includes(g));
                                    } else if (typeof entity.groups === 'string') {
                                      return remainingGroups.includes(entity.groups);
                                    }
                                    return true;
                                  });
                                });
                                
                                setEditingConstraint({
                                  ...editingConstraint, 
                                  selectedGroups: selectedGroups,
                                  entities: updatedEntities
                                });
                              }}
                              placeholder="Select groups..."
                              multiple={true}
                              optionLabel="name"
                              optionValue="id"
                              size="large"
                            />
                            {(editingConstraint?.selectedGroups || []).length > 0 && (
                              <div style={{ marginTop: '1rem' }}>
                                <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                  Selected Groups:
                                </h6>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                  {(editingConstraint?.selectedGroups || []).map(group => (
                                    <span key={group} style={{
                                      background: '#10b981',
                                      color: 'white',
                                      padding: '0.5rem 1rem',
                                      borderRadius: '20px',
                                      fontSize: '0.85rem',
                                      fontWeight: 500,
                                      cursor: 'pointer'
                                    }} onClick={() => toggleGroupSelection(group, true)}>
                                      {group} ✕
                                    </span>
                                  ))}
                                </div>
                                
                                {/* Preview of entities that will be added by groups */}
                                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                  <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                    Entities from selected groups:
                                  </h6>
                                  <div style={{ fontSize: '0.8rem', color: '#374151' }}>
                                    {(() => {
                                      const groupEntities = (editingConstraint?.selectedGroups || []).flatMap(group => {
                                        return getCurrentEntities().filter(entity => {
                                          if (Array.isArray(entity.groups)) {
                                            return entity.groups.includes(group);
                                          } else if (typeof entity.groups === 'string') {
                                            return entity.groups === group;
                                          }
                                          return false;
                                        });
                                      });
                                      
                                      const uniqueEntities = [...new Set(groupEntities.map(e => e.id))].map(id => 
                                        groupEntities.find(e => e.id === id)
                                      );
                                      
                                      if (uniqueEntities.length === 0) {
                                        return 'No entities found for selected groups';
                                      }
                                      
                                      return uniqueEntities.map(entity => entity.name).join(', ');
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '1.5rem' 
                          }}>
                            <h4 style={{ 
                              margin: 0, 
                              fontSize: '1.1rem', 
                              fontWeight: 600, 
                              color: '#667eea' 
                            }}>
                              📋 Constraint #{index + 1}
                            </h4>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => editConstraint(constraint)}
                                style={{
                                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.5rem',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => removeConstraint(constraint.id)}
                                style={{
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.5rem',
                                  color: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                          
                          {/* Constraint Details */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                                MINIMUM NUMBER
                              </div>
                              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
                                {constraint.minimumNumber}
                              </div>
                            </div>
                            <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                                ROTATION RANGE
                              </div>
                              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: '#374151' }}>
                                {constraint.fromRotation} - {constraint.toRotation}
                              </div>
                            </div>
                            <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                                {activeTab === 'pharmd' ? 'SPECIALITIES' : 'INSTITUTIONS'}
                              </div>
                              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: '#374151' }}>
                                {constraint.entities?.length || 0}
                              </div>
                            </div>
                            <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.5rem' }}>
                                GROUPS
                              </div>
                              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: '#374151' }}>
                                {constraint.selectedGroups?.length || 0}
                              </div>
                            </div>
                          </div>

                          {/* Selected specialities/institutions */}
                          {constraint.entities && constraint.entities.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                              <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                Selected {activeTab === 'pharmd' ? 'Specialities' : 'Institutions'}:
                              </h5>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {constraint.entities.map(entity => (
                                  <span key={entity.id} style={{
                                    background: 'rgba(102, 126, 234, 0.1)',
                                    color: '#667eea',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    border: '2px solid rgba(102, 126, 234, 0.2)'
                                  }}>
                                    {getEntityDisplayName(entity)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Selected groups */}
                          {constraint.selectedGroups && constraint.selectedGroups.length > 0 && (
                            <div>
                              <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
                                Selected Groups:
                              </h5>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {constraint.selectedGroups.map(group => (
                                  <span key={group} style={{
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    color: '#10b981',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '20px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    border: '2px solid rgba(16, 185, 129, 0.2)'
                                  }}>
                                    {group}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save Button */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '20px',
              padding: '2rem',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              textAlign: 'center'
            }}>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                style={{
                  background: isLoading 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '1.25rem 3rem',
                  color: 'white',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '1rem',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 30px rgba(43, 70, 99, 0.4)',
                  transform: 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 15px 35px rgba(43, 70, 99, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 10px 30px rgba(43, 70, 99, 0.4)';
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                    Saving Constraints...
                  </>
                ) : (
                  <>
                    <FaSave />
                    Save All Constraints
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConstraintsFormModern;
